/**
 * overlayDrawRecord — what each debug overlay ACTUALLY drew, written BY the
 * draw path AS it draws.
 *
 * ── WHY IT IS WRITTEN BY THE DRAWER, NEVER RECOMPUTED ON QUERY ──────────────
 * A readback that re-derives what it *thinks* the overlay drew is a second
 * source of truth, and a second source of truth is the exact defect class this
 * repo spent 2026-07-26/27 killing: the aggro overlay drew one 250px constant
 * for 110 mobs whose enforced radii span 16 values (D1), the powerup box drew
 * 32px against a 48px pickup (D2), the checkpoint overlay drew every zone type
 * (D3). Every one of them reported healthy. A recomputing readback would have
 * agreed with them and manufactured false confidence — worse than no readback.
 *
 * So this module is a dumb ledger. It holds only what a drawer handed it, and
 * it never derives, never defaults, never fills a gap. It is modelled on
 * `MeleeConePlan` (src/game/systems/combat/CombatManager.ts): one record, two
 * projections — pixels and JSON.
 *
 * Deliberately NOT an `OverlayRegistry`. No class, no lifecycle, no DI — see
 * docs/audits/2026-07-26-debug-geometry-consolidation-research.md §3.4, which
 * says a registry abstraction here would be substrate for substrate's sake.
 *
 * Three properties, baked in:
 *
 *   ZERO COST WHEN OFF — every call site sits INSIDE the overlay's existing
 *   enabled gate. An overlay that is off never calls in, so it costs nothing
 *   and its record simply goes stale.
 *
 *   BOUNDED — `drew` is capped at OVERLAY_DREW_SAMPLE_CAP with an explicit
 *   `truncated` flag. `drawnCount` stays exact (a 200-mob map reports 200 and
 *   ships 24 samples), because a capped COUNT would be a lie and the cap exists
 *   only to keep the payload small.
 *
 *   STAMPED — every record carries `lastDrawnAt` + `staleMs`. Without them a
 *   reader cannot tell "drew nothing this frame" (fresh, count 0) from "has not
 *   drawn since you flipped it on" (stale), and would call the second one
 *   verification. That is the repo's "an unproven zero is not evidence" rule
 *   applied to the tool itself.
 */

/** Max items projected into `drew`. The COUNT is never capped — only the payload. */
export const OVERLAY_DREW_SAMPLE_CAP = 24;

/**
 * How long a recorded draw stays "live".
 *
 * Per-entity drawers (MobRenderer, PlayerRenderer) run once per entity with no
 * frame hook, so there is no "pass end" at which a departed entity could be
 * removed — it ages out instead. ~30 frames at 60fps: long enough that a
 * normal frame never drops an entity that is still being drawn, short enough
 * that a despawned one disappears promptly. Whole-pass drawers overwrite their
 * entries every frame, so this only ever matters to them once drawing stops.
 */
export const OVERLAY_ENTRY_FRESH_MS = 500;

/**
 * Soft ceiling on retained keys per overlay. Not the payload bound (that is
 * OVERLAY_DREW_SAMPLE_CAP) — just a guard so a churning key space (respawning
 * mobs) can't grow the map without end between prunes.
 */
const MAX_RETAINED_KEYS = 512;

/** One overlay's draw ledger, as JSON. */
export interface OverlayDrawRecord {
  /** Exact number of live recorded draws — NOT capped by the sample cap. */
  drawnCount: number;
  /** Bounded sample of the VALUES drawn, so a verdict is assertable from JSON. */
  drew: unknown[];
  /** True when `drawnCount > drew.length`. */
  truncated: boolean;
  /** Epoch ms of the most recent write; null when the drawer has never run. */
  lastDrawnAt: number | null;
  /** Age of `lastDrawnAt`; null when never drawn. A large value means STALE. */
  staleMs: number | null;
  /** Pass-level facts that aren't per-item (e.g. the resolved melee-cone spec). */
  detail?: Record<string, unknown>;
}

interface Entry {
  item: unknown;
  at: number;
}

interface Ledger {
  entries: Map<string, Entry>;
  detail?: Record<string, unknown>;
  lastDrawnAt: number;
}

const ledgers = new Map<string, Ledger>();

function ensure(overlayId: string): Ledger {
  let ledger = ledgers.get(overlayId);
  if (!ledger) {
    ledger = { entries: new Map(), lastDrawnAt: 0 };
    ledgers.set(overlayId, ledger);
  }
  return ledger;
}

function prune(ledger: Ledger, now: number): void {
  for (const [key, entry] of ledger.entries) {
    if (now - entry.at > OVERLAY_ENTRY_FRESH_MS) ledger.entries.delete(key);
  }
}

/**
 * Record ONE drawn item, keyed by the thing it describes (entity id, zone id).
 *
 * For drawers that run per entity with no per-frame hook — `MobRenderer`
 * mints one instance per mob, so no single call site sees the whole set.
 * Re-drawing the same key refreshes it in place.
 *
 * MUST be called from inside the overlay's enabled gate.
 */
export function recordOverlayEntity(overlayId: string, key: string, item: unknown): void {
  const ledger = ensure(overlayId);
  const now = Date.now();
  ledger.entries.set(key, { item, at: now });
  ledger.lastDrawnAt = now;
  if (ledger.entries.size > MAX_RETAINED_KEYS) prune(ledger, now);
}

/**
 * Record a COMPLETE draw pass, replacing whatever the overlay drew last frame.
 *
 * For drawers that see their whole set at one call site. Passing `[]` is
 * meaningful and encouraged: it stamps "this overlay ran and drew nothing",
 * which is a different fact from "this overlay has not run".
 *
 * MUST be called from inside the overlay's enabled gate.
 */
export function recordOverlayPass(
  overlayId: string,
  items: readonly unknown[],
  detail?: Record<string, unknown>,
): void {
  const ledger = ensure(overlayId);
  const now = Date.now();
  ledger.entries.clear();
  items.forEach((item, i) => ledger.entries.set(String(i), { item, at: now }));
  ledger.detail = detail;
  ledger.lastDrawnAt = now;
}

/** The ledger for one overlay, as JSON. Never invents a draw that didn't happen. */
export function readOverlayDrawRecord(overlayId: string): OverlayDrawRecord {
  const ledger = ledgers.get(overlayId);
  if (!ledger || ledger.lastDrawnAt === 0) {
    return { drawnCount: 0, drew: [], truncated: false, lastDrawnAt: null, staleMs: null };
  }
  const now = Date.now();
  prune(ledger, now);
  const live = [...ledger.entries.values()];
  return {
    drawnCount: live.length,
    drew: live.slice(0, OVERLAY_DREW_SAMPLE_CAP).map((e) => e.item),
    truncated: live.length > OVERLAY_DREW_SAMPLE_CAP,
    lastDrawnAt: ledger.lastDrawnAt,
    staleMs: Math.max(0, now - ledger.lastDrawnAt),
    ...(ledger.detail ? { detail: ledger.detail } : {}),
  };
}

/** Drop everything. Test hygiene only — nothing in the game calls this. */
export function resetOverlayDrawRecords(): void {
  ledgers.clear();
}
